import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Select,
  Alert,
  Loader,
} from '@vipso/shared';
import { useInitAppMutation } from '../api/tenantApi';
import { setAuthFromInit } from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';
import type { RootState } from '../store';

export function OrgPickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const orgs = useSelector((state: RootState) => state.auth.orgs);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [initApp, { isLoading, error }] = useInitAppMutation();

  const autoOrgId = (location.state as { autoOrgId?: string } | null)?.autoOrgId;

  // Only the user's memberships — never the public tenant org list (cross-tenant leak).
  const options = useMemo(
    () => orgs.map((org) => ({ value: org.orgId, label: org.name || org.orgId })),
    [orgs],
  );

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (autoOrgId) {
      setSelectedOrgId(autoOrgId);
    } else if (options.length === 1) {
      setSelectedOrgId(options[0].value);
    }
  }, [user, options, autoOrgId, navigate]);

  useEffect(() => {
    if (autoOrgId && user && !isLoading) {
      void handleContinue(autoOrgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOrgId, user]);

  async function handleContinue(orgId?: string) {
    const targetOrgId = orgId || selectedOrgId;
    if (!user || !targetOrgId) return;

    try {
      const result = await initApp({ userId: user.id, orgId: targetOrgId }).unwrap();

      dispatch(setAuthFromInit(mapInitAppPayload(result.initApp, { user })));

      navigate('/dashboard', { replace: true });
    } catch {
      // surfaced via error state
    }
  }

  if (!user) return null;

  if (autoOrgId && isLoading) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Loader />
        <Text c="dimmed">{t('orgPicker.starting')}</Text>
      </Stack>
    );
  }

  return (
    <Stack align="center" justify="center" style={{ minHeight: '100vh' }} p="md">
      <Paper p="xl" withBorder w="100%" maw={480}>
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={2}>{t('orgPicker.title')}</Title>
            <Text c="dimmed" size="sm">
              {t('orgPicker.subtitle', { name: user.name })}
            </Text>
          </Stack>

          {error ? (
            <Alert color="red" title={t('orgPicker.initFailed')}>
              {t('orgPicker.initFailedHint')}
            </Alert>
          ) : null}

          {options.length === 0 ? (
            <Alert color="yellow" title={t('orgPicker.noOrgs')}>
              {t('orgPicker.noOrgsHint')}
            </Alert>
          ) : (
            <Select
              label={t('orgPicker.organization')}
              placeholder={t('orgPicker.selectPlaceholder')}
              value={selectedOrgId}
              onChange={(value) => setSelectedOrgId(value || '')}
              data={options}
              searchable
            />
          )}

          <Button
            loading={isLoading}
            disabled={!selectedOrgId}
            onClick={() => void handleContinue()}
            fullWidth
          >
            {t('common.continue')}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
